import { useState, useEffect, useRef, useCallback } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { getPdfLink, getPlan } from "api/dietPlan.js";
import usePlanTimer from "@utils/DietPlanProgress.js";
import { getInitialTab } from "@utils/getInitialTab.js";
import { resetAllMealsToShow } from "Redux/slices/selectedMealsSlice.js";
import { genratePlanCheck, isPendingMeal } from "@constants/dietPlan";
import { initialSelectMealList } from "data/dietPlanConst.js/index.js";

export const useDietPlanData = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Refs
  const isMounted = useRef(false);
  const timeoutRef = useRef(null);

  // --- Added Missing Recipe Data States ---
  const [recipeData, setRecipeData] = useState(null);

  // Loading & Meta States
  const [isMealPlanLoading, setIsMealPlanLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true);
  const [message, setMessage] = useState("25");
  const [pdfGenerating, setPdfGenerating] = useState(true);
  const [dietPdfUrl, setDietPdfUrl] = useState(null);

  // Plan Status States
  const [planPending, setPlanPending] = useState(false);
  const [recreatePlan, setRecreatePlan] = useState(false);
  const [planInitiateTime, setPlanInitiateTime] = useState(null);

  // UI States
  const [selectedDay, setSelectedDay] = useState(() => dayjs().format("dddd"));
  const [date, setDate] = useState(dayjs());
  const [changeInDay, setChangeInDay] = useState(null);
  const [tab, setTab] = useState(getInitialTab);

  // Data States
  const [adviceList, setAdviceList] = useState({});
  const [mealList, setMealList] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [weeklyMeals, setWeeklyMeals] = useState([]);
  const [initialSelectedMeal, setInitialSelectedMeal] = useState([]);
  const [startDate, setStartDate] = useState(null);

  const { timeLeft, setTimeLeft, stopCountdown } = usePlanTimer(
    planInitiateTime,
    1200
  );

  // --- Helper Functions ---

  const orderDaysGet = (CommingDate) => {
    return Array.from({ length: 7 }, (_, i) => {
      const currentDate = new Date(CommingDate);
      currentDate.setDate(currentDate.getDate() + i);
      return {
        day: currentDate.toLocaleDateString("en-US", { weekday: "long" }),
      };
    });
  };

  const extractAllImages = (dayImages) => {
    const snacks = dayImages?.Snacks || {};
    return {
      Breakfast: dayImages?.Meals?.Breakfast || [],
      Lunch: dayImages?.Meals?.Lunch || [],
      Dinner: dayImages?.Meals?.Dinner || [],
      afternoon_snacks: snacks["Afternoon Snacks"] || null,
      evening_snacks: snacks["Evening Snacks"] || null,
    };
  };

  const getUrl = async (retryCount = 0) => {
    try {
      if (!isMounted.current) return;
      const response = await getPdfLink();
      const url = response?.diet_pdf;

      if (url) {
        setDietPdfUrl(url);
        setPdfGenerating(false);
      } else if (retryCount < 2) {
        timeoutRef.current = setTimeout(() => getUrl(retryCount + 1), 30000);
      } else {
        setPdfGenerating(false);
      }
    } catch (error) {
      if (!isMounted.current) return;
      setPdfGenerating(false);
    }
  };

  const handleSetDietPlan = useCallback((data) => {
    if (!isMounted.current) return;
    setPlanInitiateTime(data?.start_time);
    setMessage(data?.loader_status);

    if (data?.diet_plan) {
      const diet_plan = data?.diet_plan;
      const diet_pdf_url = data?.diet_pdf;
      const planStartDate = data?.plan_start_date;
      const dayOrderStrings = orderDaysGet(planStartDate).map((item) => item.day);

      const dietPlanSorted = Object.fromEntries(
        Object.entries(diet_plan).sort((a, b) => {
          const indexA = dayOrderStrings.indexOf(a[0]);
          const indexB = dayOrderStrings.indexOf(b[0]);
          return indexA - indexB;
        })
      );

      const images = data?.images || [];
      setPlanPending(data?.is_pending === isPendingMeal.ALLOWED);
      setRecreatePlan(data?.is_pending === isPendingMeal.NOT_ALLOWED);

      if (diet_pdf_url) {
        setDietPdfUrl(diet_pdf_url);
        setPdfGenerating(false);
      } else {
        setTimeout(() => getUrl(), 30000);
      }

      const selectedDiet_plan = data?.selected_diet_plan ?? initialSelectMealList;

      // Logic mapping (Advice, Meals, Recipes, etc.)
      const updatedAdviceList = Object.keys(dietPlanSorted).reduce((acc, day) => {
        const tip = dietPlanSorted[day]?.["Daily Health Tip"];
        acc[day] = {
          tip: tip?.Tip || "",
          details: tip?.Explanation || "",
          time: tip?.TimeRequired || "",
          date: dietPlanSorted[day].Date,
        };
        return acc;
      }, {});

      const updatedMealList = Object.keys(dietPlanSorted).map((day, index) => {
        const imageSet = extractAllImages(images[day]);
        const dayData = dietPlanSorted[day];
        return {
          day,
          date: dayData.Date,
          meal: dayData.Meals ?? "No Meal",
          images: imageSet ?? {},
          color: index % 2 === 0 ? "bg-pink-500" : "bg-blue-500",
          snacks: {
            afternoon_snacks: dayData.Snacks?.["Afternoon Snacks"] || null,
            evening_snacks: dayData.Snacks?.["Evening Snacks"] || null,
          },
        };
      });

      const updatedRecipes = Object.keys(dietPlanSorted).map((day) => {
        const dayData = dietPlanSorted[day];
        const meals = dayData?.Meals;
        const snacks = dayData?.Snacks;
        const selected = selectedDiet_plan?.selectedMeals?.[day] || {};

        const getMeal = (type) => meals?.[type][selected[type.toLowerCase()] ?? 0] || null;
        const getImage = (type) => {
            const list = images?.[day]?.["Meals"]?.[type];
            return (Array.isArray(list) ? list[selected[type.toLowerCase()] ?? 0] : null) || "/MealOne.png";
        }

        const breakfast = getMeal("Breakfast");
        const lunch = getMeal("Lunch");
        const dinner = getMeal("Dinner");
        const totalCalories = (breakfast?.Calories || 0) + (lunch?.Calories || 0) + (dinner?.Calories || 0) + (snacks["Afternoon Snacks"]?.Calories || 0) + (snacks["Evening Snacks"]?.Calories || 0);

        return {
          image: getImage("Breakfast"),
          title: day,
          date: dayData.Date,
          description: [`${breakfast?.Calories || 0} Kcal`, `${lunch?.Calories || 0} Kcal`, `${dinner?.Calories || 0} Kcal`],
          calories: `${totalCalories} Kcal`,
          tip: dayData["Daily Health Tip"]?.Tip || null,
          tipExplanation: dayData["Daily Health Tip"]?.Explanation || null,
          snacks: { afternoon_snacks: snacks["Afternoon Snacks"] || null, evening_snacks: snacks["Evening Snacks"] || null },
        };
      });

      const updatedWeeklyMeal = Object.keys(dietPlanSorted).map((day) => {
        const dayData = dietPlanSorted[day];
        const meals = dayData?.Meals;
        const snacks = dayData?.Snacks;
        const selected = selectedDiet_plan?.selectedMeals?.[day] || {};

        const getMeal = (type) => meals?.[type][selected[type.toLowerCase()] ?? 0] || null;
        const getImage = (type) => {
            const list = images?.[day]?.["Meals"]?.[type];
            return (Array.isArray(list) ? list[selected[type.toLowerCase()] ?? 0] : null) || "/MealOne.png";
        };
        const getSnacksImg = (type, time) => images?.[day]?.[type]?.[time] || "/MealOne.png";

        const b = getMeal("Breakfast");
        const l = getMeal("Lunch");
        const d = getMeal("Dinner");
        const total = (b?.Calories || 0) + (l?.Calories || 0) + (d?.Calories || 0);

        return {
          image: { breakfast: getImage("Breakfast"), lunch: getImage("Lunch"), dinner: getImage("Dinner"), afternoon_snacks: getSnacksImg("Snacks", "Afternoon Snacks"), evening_snacks: getSnacksImg("Snacks", "Evening Snacks") },
          title: day,
          date: dayData.Date,
          meal_name: { breakfast: b?.MealName || "", lunch: l?.MealName || "", dinner: d?.MealName || "" },
          description: { breakfast: b?.Description || "", lunch: l?.Description || "", dinner: d?.Description || "" },
          summary: { breakfast: b?.Summary || "", lunch: l?.Summary || "", dinner: d?.Summary || "" },
          ingredients: { breakfast: b?.Ingredients || [], lunch: l?.Ingredients || [], dinner: d?.Ingredients || [] },
          preparation_time: { breakfast: b?.PreparationTime || "", lunch: l?.PreparationTime || "", dinner: d?.PreparationTime || "" },
          calories: { breakfast: b?.Calories || 0, lunch: l?.Calories || 0, dinner: d?.Calories || 0, total: `${total} kcal` },
          nutrition: { breakfast: b?.Nutritions, lunch: l?.Nutritions, dinner: d?.Nutritions },
          serving: { breakfast: b?.Serving || 1, lunch: l?.Serving || 1, dinner: d?.Serving || 1 },
          tip: { breakfast: b?.Tip || "", lunch: l?.Tip || "", dinner: d?.Tip || "" },
          snacks: { afternoon_snacks: snacks["Afternoon Snacks"] || null, evening_snacks: snacks["Evening Snacks"] || null },
        };
      });

      setAdviceList(updatedAdviceList);
      setMealList(updatedMealList);
      setInitialSelectedMeal(selectedDiet_plan);
      setRecipes(updatedRecipes);
      setWeeklyMeals(updatedWeeklyMeal);
      setStartDate(planStartDate);

      if (data?.is_plan_generated === genratePlanCheck.ALLOWED) {
        navigate("/diet-plan/select-meals", { state: { diet_plan: true } });
      }
      if (data?.is_pending === isPendingMeal.NOT_ALLOWED) setPlanPending(false);
      setIsMealPlanLoading(false);
    } else {
      if (data?.is_pending) {
        setPlanPending(true);
        setIsMealPlanLoading(true);
      } else {
        navigate("/diet-plan/preferences", { state: { dietPlan: true, isReacreate: data?.diet_plan } });
      }
    }
  }, [navigate]);

  const fetchDietPlan = async (commingDate = null) => {
    if (!isMounted.current) return;
    try {
      const params = {};
      if (commingDate) {
        const newDate = new Date(commingDate);
        params.date = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, "0")}-${String(newDate.getDate()).padStart(2, "0")} 00:00:00`;
      }
      const response = await getPlan(params);
      handleSetDietPlan(response);
      if (commingDate) {
        setSelectedDay(dayjs(commingDate).format("dddd"));
        setChangeInDay(commingDate);
      }
      setTimeout(() => setFirstLoad(false), 1000);
    } catch (error) {
      if (!isMounted.current) return;
      navigate("/diet-plan/preferences", { state: { dietPlan: true, isReacreate: false } });
    }
  };

  const handleDietPlanFailed = () => {
    setIsMealPlanLoading(false);
    setPlanPending(false);
    setRecreatePlan(true);
    setFirstLoad(false);
    if (!weeklyMeals.length) {
      navigate("/diet-plan/preferences", { state: { dietPlan: true, isReacreate: false } });
    }
  };

  useEffect(() => {
    isMounted.current = true;
    dispatch(resetAllMealsToShow());
    fetchDietPlan();
    return () => {
      isMounted.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return {
    isMealPlanLoading, firstLoad, recipeData, setRecipeData, dietPdfUrl,
    planPending, recreatePlan, pdfGenerating, message, setMessage,
    selectedDay, setSelectedDay, date, setDate, changeInDay, planInitiateTime,
    adviceList, mealList, recipes, weeklyMeals, initialSelectedMeal,
    startDate, timeLeft, setTimeLeft, stopCountdown, tab, setTab,
    fetchDietPlan, handleDietPlanFailed, handleSetDietPlan, t
  };
};